import type { ComponentType } from 'react';
import { NavLink } from 'react-router-dom';

import Logo from '@/components/common/Logo';
import cn from '@/utils/cn';

export interface AdminNavItem {
  title: string;
  url: string;
  icon: ComponentType<{ className?: string }>;
  end?: boolean;
}

// Fixed 36px row height so a collapsed row (icon only) and an expanded row
// (icon + text-sm label) measure the same - otherwise the list shifts up and
// grows downward every time the sidebar toggles.
const NAV_ROW =
  'flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-sm font-semibold transition-colors';

interface AdminSidebarProps {
  navItems: AdminNavItem[];
  collapsed: boolean;
  homeUrl: string;
}

export const AdminSidebar = ({ navItems, collapsed, homeUrl }: AdminSidebarProps) => (
  <aside
    className={cn(
      'flex shrink-0 flex-col border-r border-sidebar-border bg-sidebar transition-[width] duration-200',
      collapsed ? 'w-16' : 'w-60',
    )}
  >
    <div className="flex h-14 items-center border-b border-sidebar-border px-3">
      <Logo to={homeUrl} showWordmark={!collapsed} />
    </div>

    <nav className="flex-1 overflow-y-auto p-2">
      <ul className="space-y-1">
        {navItems.map((item) => (
          <li key={item.title}>
            <NavLink
              to={item.url}
              end={item.end}
              title={item.title}
              className={({ isActive }) =>
                cn(
                  NAV_ROW,
                  collapsed && 'justify-center',
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                    : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                )
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="truncate">{item.title}</span>}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  </aside>
);
